import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * BagStockTransfer Component
 *
 * Inventory Manager can transfer stock from one bag to another.
 *
 * APIs used:
 *  GET  /api/inventory/inventory-bags/
 *  GET  /api/inventory/inventory-items/
 *  POST /api/inventory/bag-stock-transfer/
 *  GET  /api/inventory/bag-stock-transfer/         (transfer history)
 */

const BASE_URL = 'https://cbackend.xraidigital.com';
// const BASE_URL = 'http://127.0.0.1:8000';
const TOKEN    = localStorage.getItem('token');
const HARDCODED_TOKEN = "c6108d5ff4c46c8f3fe311813519d09e1d4bf420";
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    Authorization: `Token ${TOKEN || HARDCODED_TOKEN}`,
  },
});

// ─── helpers ─────────────────────────────────────────────────────────────────

const Spinner = ({ color = 'border-indigo-600', size = 'h-6 w-6' }) => (
  <div className={`animate-spin rounded-full ${size} border-b-2 ${color}`} />
);

const Alert = ({ type, message, onClose }) => {
  const styles = {
    success: 'bg-green-50  border-green-400 text-green-800',
    error:   'bg-red-50    border-red-400   text-red-800',
    info:    'bg-blue-50   border-blue-400  text-blue-800',
  };
  return (
    <div className={`flex items-start gap-3 border-l-4 rounded-lg p-4 mb-4 ${styles[type]}`}>
      <span className="mt-0.5 text-lg">
        {type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}
      </span>
      <p className="flex-1 text-sm font-medium">{message}</p>
      {onClose && (
        <button onClick={onClose} className="text-inherit opacity-60 hover:opacity-100 text-lg leading-none">×</button>
      )}
    </div>
  );
};

const formatDate = (str) => {
  if (!str) return '—';
  try {
    return new Date(str).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
};

// ─── Transfer Form ────────────────────────────────────────────────────────────

const TransferForm = ({ bags, items, onSuccess }) => {
  const [fromBagId, setFromBagId]     = useState('');
  const [toBagId, setToBagId]         = useState('');
  const [itemId, setItemId]           = useState('');
  const [quantity, setQuantity]       = useState('');
  const [remarks, setRemarks]         = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);

  // Stock info for the selected source bag + item
  const [sourceStock, setSourceStock] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);

  // When from_bag + item both selected, look up remaining stock
  useEffect(() => {
    if (!fromBagId || !itemId) { setSourceStock(null); return; }
    const bag = bags.find(b => String(b.id) === fromBagId);
    if (!bag) return;
    const techItem = bag.inventory_items?.find(
      ti => String(ti.inventory_item) === itemId || String(ti.inventory_item?.id) === itemId
    );
    setSourceStock(techItem ? techItem.quantity_remaining : null);
  }, [fromBagId, itemId, bags]);

  const handleSubmit = async () => {
    setError(null);
    if (!fromBagId || !toBagId || !itemId || !quantity) {
      setError('All fields are required.'); return;
    }
    if (fromBagId === toBagId) {
      setError('Source and destination bags must be different.'); return;
    }
    if (Number(quantity) <= 0) {
      setError('Quantity must be greater than 0.'); return;
    }
    if (sourceStock !== null && Number(quantity) > sourceStock) {
      setError(`Insufficient stock. Available in source bag: ${sourceStock} units.`); return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/api/inventory/bag-stock-transfer/', {
        from_bag_id:       Number(fromBagId),
        to_bag_id:         Number(toBagId),
        inventory_item_id: Number(itemId),
        quantity:          Number(quantity),
        remarks,
      });
      // Reset form
      setFromBagId(''); setToBagId(''); setItemId('');
      setQuantity(''); setRemarks(''); setSourceStock(null);
      onSuccess(res.data);
    } catch (err) {
      const detail =
        err.response?.data?.detail
        || (typeof err.response?.data === 'object'
          ? Object.values(err.response.data).flat().join(' ')
          : err.message)
        || 'Transfer failed.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedItem = items.find(i => String(i.id) === itemId);

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-inner">
          <span className="text-white text-lg">⇄</span>
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-800">Transfer Stock</h2>
          <p className="text-sm text-gray-500">Move items between technician bags</p>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}

      <div className="grid md:grid-cols-2 gap-5">

        {/* Source Bag */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            From Bag <span className="text-red-500">*</span>
          </label>
          <select
            value={fromBagId}
            onChange={e => { setFromBagId(e.target.value); setSourceStock(null); setError(null); }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          >
            <option value="">— Select source bag —</option>
            {bags.map(b => (
              <option key={b.id} value={b.id}>
                BAG-{b.id} · {b.current_technician_details?.name || 'N/A'} · {b.month}/{b.year}
              </option>
            ))}
          </select>
        </div>

        {/* Destination Bag */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            To Bag <span className="text-red-500">*</span>
          </label>
          <select
            value={toBagId}
            onChange={e => { setToBagId(e.target.value); setError(null); }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          >
            <option value="">— Select destination bag —</option>
            {bags
              .filter(b => String(b.id) !== fromBagId)
              .map(b => (
                <option key={b.id} value={b.id}>
                  BAG-{b.id} · {b.current_technician_details?.name || 'N/A'} · {b.month}/{b.year}
                </option>
              ))}
          </select>
        </div>

        {/* Inventory Item */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Inventory Item <span className="text-red-500">*</span>
          </label>
          <select
            value={itemId}
            onChange={e => { setItemId(e.target.value); setError(null); }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
          >
            <option value="">— Select item —</option>
            {items.map(item => (
              <option key={item.id} value={item.id}>
                {item.item_name} ({item.item_code})
              </option>
            ))}
          </select>
          {selectedItem && (
            <p className="text-xs text-gray-400 mt-1">
              Unit: {selectedItem.unit} · Category: {selectedItem.category}
            </p>
          )}
        </div>

        {/* Quantity */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Quantity <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={e => { setQuantity(e.target.value); setError(null); }}
            placeholder="e.g. 20"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          {/* Source stock indicator */}
          {sourceStock !== null && (
            <div className={`mt-1.5 text-xs font-medium flex items-center gap-1 ${
              Number(quantity) > sourceStock ? 'text-red-500' : 'text-green-600'
            }`}>
              <span>{Number(quantity) > sourceStock ? '⚠' : '✓'}</span>
              Available in source bag: <span className="font-bold">{sourceStock}</span> units
            </div>
          )}
          {fromBagId && itemId && sourceStock === null && (
            <p className="text-xs text-amber-600 mt-1">
              ⚠ This item may not be in the source bag.
            </p>
          )}
        </div>

      </div>

      {/* Visual transfer arrow (shows when both bags selected) */}
      {fromBagId && toBagId && (
        <div className="mt-5 bg-orange-50 border border-orange-100 rounded-xl p-4 flex items-center gap-4 text-sm flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 mb-0.5">From</p>
            <p className="font-semibold text-gray-800 truncate">
              {bags.find(b => String(b.id) === fromBagId)?.current_technician_details?.name || `BAG-${fromBagId}`}
            </p>
          </div>
          <div className="text-orange-500 text-2xl font-bold flex-shrink-0">
            {quantity ? `──── ${quantity} ────▶` : '────────▶'}
          </div>
          <div className="flex-1 min-w-0 text-right">
            <p className="text-xs text-gray-500 mb-0.5">To</p>
            <p className="font-semibold text-gray-800 truncate">
              {bags.find(b => String(b.id) === toBagId)?.current_technician_details?.name || `BAG-${toBagId}`}
            </p>
          </div>
        </div>
      )}

      {/* Remarks */}
      <div className="mt-5">
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Remarks (optional)</label>
        <textarea
          value={remarks}
          onChange={e => setRemarks(e.target.value)}
          placeholder="e.g. Transferred for additional camp requirement"
          rows={2}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
        />
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="px-6 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
        >
          {submitting && <Spinner color="border-white" size="h-4 w-4" />}
          {submitting ? 'Transferring…' : '⇄ Transfer Stock'}
        </button>
      </div>
    </div>
  );
};

// ─── Transfer History ─────────────────────────────────────────────────────────

const TransferHistory = ({ latestTransfer }) => {
  const [transfers, setTransfers]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [fromFilter, setFromFilter]   = useState('');
  const [toFilter, setToFilter]       = useState('');
  const [search, setSearch]           = useState('');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (fromFilter) params.append('from_bag_id', fromFilter);
      if (toFilter)   params.append('to_bag_id', toFilter);
      const res = await api.get(`/api/inventory/bag-stock-transfer/?${params}`);
      setTransfers(res.data.results || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load transfer history.');
    } finally {
      setLoading(false);
    }
  }, [fromFilter, toFilter]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Reload when a new transfer is made
  useEffect(() => {
    if (latestTransfer) fetchHistory();
  }, [latestTransfer, fetchHistory]);

  const filtered = transfers.filter(t =>
    t.item_name?.toLowerCase().includes(search.toLowerCase())
    || t.from_bag_label?.toLowerCase().includes(search.toLowerCase())
    || t.to_bag_label?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="w-1 h-6 bg-orange-500 rounded-full" />
          <h2 className="text-lg font-semibold text-gray-800">Transfer History</h2>
          {!loading && (
            <span className="ml-1 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full px-2 py-0.5">
              {filtered.length}
            </span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search item / bag…"
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 w-44"
          />
          <button
            onClick={fetchHistory}
            className="px-3 py-1.5 rounded-lg bg-orange-50 text-orange-600 text-sm font-medium hover:bg-orange-100 border border-orange-200"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-14 gap-3 text-gray-500">
            <Spinner color="border-orange-500" />
            <span className="text-sm">Loading history…</span>
          </div>
        ) : error ? (
          <div className="text-center py-10">
            <p className="text-red-500 text-sm mb-2">{error}</p>
            <button onClick={fetchHistory} className="text-sm text-orange-600 hover:text-orange-800 font-medium">
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-gray-400">
            <span className="text-4xl mb-2">📦</span>
            <p className="text-sm">No transfers found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Item', 'Qty', 'From Bag', 'To Bag', 'Transferred By', 'Date', 'Remarks'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-50">
                {filtered.map(t => (
                  <tr key={t.id} className="hover:bg-orange-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">#{t.id}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {t.item_name}
                      <span className="ml-1 text-xs text-gray-400">({t.item_unit})</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-orange-600 whitespace-nowrap">
                      ⇄ {t.quantity}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {t.from_bag_label || `BAG-${t.from_bag}`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {t.to_bag_label || `BAG-${t.to_bag}`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {t.transferred_by_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(t.transferred_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                      {t.remarks || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const BagStockTransfer = () => {
  const [bags, setBags]           = useState([]);
  const [items, setItems]         = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [toast, setToast]         = useState(null);
  const [latestTransfer, setLatestTransfer] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [bagsRes, itemsRes] = await Promise.all([
          api.get('/api/inventory/inventory-bags/?status=ACTIVE&page_size=100'),
          api.get('/api/inventory/inventory-items/?is_active=true&page_size=200'),
        ]);
        setBags(bagsRes.data.results || []);
        setItems(itemsRes.data.results || []);
      } catch (err) {
        console.error('Error loading bags/items:', err);
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, []);

  const handleTransferSuccess = (result) => {
    const msg = result.message || `Transferred ${result.data?.quantity} × ${result.data?.item} successfully.`;
    setToast(msg);
    setLatestTransfer(result);
    setTimeout(() => setToast(null), 5000);
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <Spinner color="border-orange-500" size="h-10 w-10" />
          <p className="text-sm">Loading inventory data…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">

        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Bag-to-Bag Stock Transfer
          </h1>
          <p className="text-gray-500 mt-1">
            Transfer inventory between technician bags. Only Inventory Managers can perform transfers.
          </p>
        </div>

        {/* Toast */}
        {toast && <Alert type="success" message={toast} onClose={() => setToast(null)} />}

        {/* Transfer Form */}
        <div className="mb-6">
          <TransferForm
            bags={bags}
            items={items}
            onSuccess={handleTransferSuccess}
          />
        </div>

        {/* Transfer History */}
        <TransferHistory latestTransfer={latestTransfer} />

      </div>
    </div>
  );
};

export default BagStockTransfer;